import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/features/auth/hooks/useAuth";
import { RBAC_STALE_TIME, rbacKeys } from "@/features/rbac/hooks/rbacKeys";
import type {
  CreateRoleInput,
  RbacRole,
  UpdateRoleInput,
} from "@/features/rbac/schemas/rbac";
import {
  createRole,
  deleteRole,
  ensureSystemRoles,
  listRoles,
  updateRole,
} from "@/features/rbac/services/rolesService";

/** Todos los roles definidos. Es una colección pequeña: se trae entera. */
export function useRoles() {
  return useQuery<RbacRole[]>({
    queryKey: rbacKeys.roles(),
    queryFn: listRoles,
    staleTime: RBAC_STALE_TIME,
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();

  return useMutation({
    mutationFn: (input: CreateRoleInput) => createRole(input, currentUser?.uid ?? null),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: rbacKeys.all });
    },
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();

  return useMutation({
    mutationFn: ({ roleId, data }: { roleId: string; data: UpdateRoleInput }) =>
      updateRole(roleId, data, currentUser?.uid ?? null),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: rbacKeys.all });
    },
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roleId: string) => deleteRole(roleId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: rbacKeys.all });
    },
  });
}

/** Vuelve a crear los roles del sistema que falten, sin tocar los existentes. */
export function useEnsureSystemRoles() {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();

  return useMutation({
    mutationFn: () => ensureSystemRoles(currentUser?.uid ?? null),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: rbacKeys.all });
    },
  });
}
